/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import Chip from '@mui/material/Chip';
import { useTheme, useMediaQuery } from "@mui/material";

// OL
import { transform } from "ol/proj";

// CUSTOM
import WeatherCard from "@/views/GIS/MapPage/WeatherCard";
import {
  InfoPaneShareActionButton,
  InfoPaneAddActionButton,
  InfoPaneCommentActionButton,
  InfoPaneUrlActionButton,
  InfoPaneWeatherActionButton,
  InfoPaneActionButtons
}
  from "@/ui/PublicPage/PublicActionButtons/";
import { sifrarnici } from "@/lib/sifrarnici";
import { prepareCoordinatesForDisplay } from "@/lib/olHelpers";
import GeoURI from "@/ui/PublicPage/SidebarPanes/GeoURI";
import POIInfoMultimedia from "@/ui/PublicPage/SidebarPanes/POIInfoMultimedia";
import SidebarInfoBoxPOIObjekti from "@/ui/PublicPage/SidebarInfoBoxPoiObjekti";

// Types
import { IInfoPane } from "@/@types/views/GIS/map";

type POIInfoPaneType = {
  infoPane?: IInfoPane;
  handleAdd: (coord: number[] | undefined, label: string) => void;
  handleShowCommentPane: (newCommentPane: object) => void;
  onOpenObilaznica?: (id: number, poi_id: number) => void;
};

function POIInfo(props: POIInfoPaneType) {
  const [fetchWeatherData, setFetchWeatherData] = useState(false);

  const { infoPane, handleShowCommentPane, handleAdd, onOpenObilaznica } = props;

  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const isMobile: boolean = useMediaQuery(theme.breakpoints.down("md"));

  const visina = infoPane?.data.record?.visina_m as number; // will be undefined when non existant
  const poi_tip_id = infoPane?.data.record?.poi_tip_id;
  const is_hps_objekt = poi_tip_id === sifrarnici.poi_tipovi.planinarska_kuca || poi_tip_id === sifrarnici.poi_tipovi.planinarski_dom || poi_tip_id === sifrarnici.poi_tipovi.planinarsko_skloniste;
  const hps_info_url = infoPane?.data.record?.hps_info_url as string; // will be null when non existant

  const coordinates = infoPane?.data.feature?.getGeometry().getCoordinates() as number[];
  const coordinatesTransformed = transform(coordinates, "EPSG:3857", "EPSG:4326");
  const displayCoordinates = prepareCoordinatesForDisplay(coordinates);

  const handleFetchWeatherData = () => {
    setFetchWeatherData(true);
  }

  useEffect(() => {
    setFetchWeatherData(false);
  }, [infoPane])

  const getTypeString = (type: number | undefined) => {
    if (!type) {
      return "";
    }

    switch (type) {
      case sifrarnici.poi_tipovi.penjaliste:
        return t("common.penjaliste");
      case sifrarnici.poi_tipovi.opasnost:
        return t("common.opasnost");
      case sifrarnici.poi_tipovi.vrh:
        return t("common.vrh");
      case sifrarnici.poi_tipovi.hgss:
        return t("common.hgss");
      case sifrarnici.poi_tipovi.odmoriste:
        return t("common.odmoriste");
      case sifrarnici.poi_tipovi.vidikovac:
        return t("common.vidikovac");
      case sifrarnici.poi_tipovi.planinarska_kuca:
        return t("common.planinarska-kuca");
      case sifrarnici.poi_tipovi.planinarski_dom:
        return t("common.planinarski-dom");
      case sifrarnici.poi_tipovi.planinarsko_skloniste:
        return t("common.planinarsko-skloniste");
      case sifrarnici.poi_tipovi.kult_mlin:
        return t("common.kult_mlin");
      case sifrarnici.poi_tipovi.kult_arheo:
        return t("common.kult_arheo");
      case sifrarnici.poi_tipovi.kult_muzej:
        return t("common.kult_muzej");
      case sifrarnici.poi_tipovi.kult_sakralni:
        return t("common.kult_sakralni");
      case sifrarnici.poi_tipovi.kult_spomenik:
        return t("common.kult_spomenik");
      case sifrarnici.poi_tipovi.kult_utvrda:
        return t("common.kult_utvrda");
      default:
        return "";
    }
  }

  const typeStr = getTypeString(infoPane?.subtype as number);

  const dmsCoordinates = `${displayCoordinates[1]}N ${displayCoordinates[0]}E`;

  const hasDesc = infoPane?.data.record?.opis_hr && infoPane?.data.record?.opis_en ? true : false;
  const desc = hasDesc && i18n.language === "hr" ? infoPane?.data.record?.opis_hr as string
    :  hasDesc && i18n.language === "en" ? infoPane?.data.record?.opis_en as string
    : ""
    ;


  return (
    <>
      {!isMobile ? (<POIInfoMultimedia mobile={isMobile} infoPane={infoPane} />) : null}
      <Box width="100%" p={2}>
        <Box>
          {infoPane?.data.record?.naziv ? (
            <Typography variant="h6" component="h2">
              {infoPane?.data.record?.naziv as String}
            </Typography>
          ) : (
            <GeoURI coords4326={coordinatesTransformed} display={dmsCoordinates} variant="body1" />
          )}
          {typeStr || visina ? (
            <Typography variant="body1" color="gray" component="p">
              {typeStr ? `${typeStr}` : ""}{typeStr && visina ? ", " : ""}{visina === undefined || visina === null ? "" : visina + " m"}</Typography>
          ) : null}
          {infoPane?.data.record?.naziv ? (
            <Box display="flex" alignItems="center" gap={1}>
            <GeoURI
              coords4326={coordinatesTransformed}
              display={dmsCoordinates}
              variant="body1"
            />
          </Box>

          ) : null} 
          {infoPane?.data.record && infoPane?.data.record !== null && infoPane?.data.record.obilaznice !== null ? (infoPane?.data.record.obilaznice as Array<any>).map(obilaznica => {
            const obLabel = i18n.language === "en" ? obilaznica.naziv_en : obilaznica.naziv;
            
            return <div key={`obilaznica-${obilaznica.id}`}>
              <Chip label={obLabel} color="primary" onClick={() => {onOpenObilaznica !== undefined ? onOpenObilaznica(obilaznica.id, infoPane?.data.record?.id as number) : null}} style={{marginTop: 5, color: '#DDFFD7'}} />
            </div>
          }) : null}
        </Box>
        {hasDesc ? (
          <Box sx={{my: 3}}>
            <Typography variant="body1">
              {desc}
            </Typography>
          </Box>
        ) : null}
        <InfoPaneActionButtons>
          <InfoPaneAddActionButton
            label={infoPane?.data.record?.naziv as string}
            initialCoordinates={coordinates} onAdd={handleAdd}
          />
          <InfoPaneShareActionButton
            infoPane={infoPane}
            object_type="poi"
          />
          <InfoPaneCommentActionButton
            objekt_type="poi"
            handleGoToComments={handleShowCommentPane}
            // @ts-ignore
            data={infoPane?.data}
          />
          <InfoPaneWeatherActionButton
            handleFetchWeatherData={handleFetchWeatherData}
          />
          {hps_info_url !== null ?
            <InfoPaneUrlActionButton
              url={hps_info_url}
            />
            :
            null
          }
        </InfoPaneActionButtons>
      </Box>
      <Box>
        <Fade in={fetchWeatherData}>
          <Box>
            <Divider variant="middle" sx={{ transition: "display 0.3s", display: fetchWeatherData ? "block" : "none", margin: 3, borderColor: "#000000" }} />
            <WeatherCard
              lat={coordinatesTransformed[1]}
              lon={coordinatesTransformed[0]}
              fetchData={fetchWeatherData}
            />
          </Box>
        </Fade>
      </Box>
      {is_hps_objekt ? (
        <>
          <Divider variant="middle" sx={{ pt: 2 }} />
          <SidebarInfoBoxPOIObjekti link={hps_info_url} />
        </>
      ) : null}
      {isMobile ? (<POIInfoMultimedia mobile={isMobile} infoPane={infoPane} />) : null}
    </>
  );
}

export default POIInfo;
